import { CombinedGraphQLErrors } from '@apollo/client/errors';

/**
 * Error codes the Go lambdas send as `errorType` on every GraphQL error
 * (fenix-domain-data/docs/lambda-conventions.md section 3.2). Branch on these, never on
 * the message text.
 */
export type GoErrorCode =
  | 'NOT_FOUND'
  | 'BAD_REQUEST'
  | 'VALIDATION'
  | 'LOCKED'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'INTERNAL';

/**
 * A failed Go lambda call. `message` is the server's sentence (several joined with "; "
 * when the gateway returned more than one), `code` its errorType and `info` its errorInfo
 * (`uid`, `lockedBy`, `violations`, ...). `messages` keeps the individual sentences for
 * the `responseMessages` shape the item pages render.
 */
export class GoApiError extends Error {
  constructor(
    message: string,
    public readonly code: GoErrorCode | undefined,
    public readonly info: Record<string, unknown> | undefined,
    public readonly messages: string[]
  ) {
    super(message);
    this.name = 'GoApiError';
  }
}

/** Builds a GoApiError from an Apollo `result.error` (errorPolicy 'all') or a thrown error. */
export function toGoApiError(error: unknown): GoApiError {
  if (error instanceof GoApiError) {
    return error;
  }
  const raw: any[] = CombinedGraphQLErrors.is(error) ? (error as any).errors : [];
  const messages: string[] = raw.length > 0
    ? raw.map(e => e.message)
    : [(error as any)?.message ?? String(error)];
  const first = raw[0];
  return new GoApiError(messages.join('; '), first?.errorType, first?.errorInfo, messages);
}

/** The errorType of a Go failure, whatever shape the error reached us in. */
export function goErrorCode(error: unknown): GoErrorCode | undefined {
  return toGoApiError(error).code;
}

/**
 * True when the item does not exist on the Go side. The text fallback stays until every
 * lambda deployment sends `errorType`; remove it afterwards.
 */
export function isGoNotFound(error: unknown): boolean {
  const e = toGoApiError(error);
  if (e.code) {
    return e.code === 'NOT_FOUND';
  }
  return /not found|no rows/i.test(e.message);
}

/** The sentences to show the user for a failed Go write. */
export function goErrorMessages(error: unknown, fallback: string): string[] {
  const e = toGoApiError(error);
  return e.messages.length > 0 && e.messages[0] ? e.messages : [fallback];
}
